import { Badge } from '@vrplatform/components';
import { Icon } from '@vrplatform/icons';
import { type Maybe, toTitleCase, utc } from '@vrplatform/utils';
import { Center, Group, Tooltip } from '@mantine/core';
import { useMemo } from 'react';

type Props = {
  status: Maybe<string>;
  cancelledAt?: string | null;
  generalLedgerStatus?: Maybe<'active' | 'inactive'>;
};

export const ReservationStatusBadge = ({
  status,
  cancelledAt,
  generalLedgerStatus,
}: Props) => {
  const { color, label, info } = useMemo(() => {
    let color: 'green' | 'yellow' | 'red' = 'yellow';
    const label = toTitleCase(status || '');
    let info: string | undefined;

    switch (status) {
      case 'booked':
        color = 'green';
        break;
      case 'cancelled':
        color = 'red';
        info = cancelledAt
          ? `Cancelled on
            ${utc(cancelledAt).format('MMM DD, YYYY')}`
          : undefined;
        break;
      case 'inquiry':
        color = 'yellow';
        break;
      case 'blocked':
        color = 'yellow';
        break;
      default:
        break;
    }

    return {
      color,
      label,
      info,
    };
  }, [status, cancelledAt]);

  if (!label) return null;

  const isArchived = generalLedgerStatus === 'inactive';

  return (
    <Group gap="xs" wrap="nowrap">
      <Badge
        color={color}
        rightIcon={
          info ? (
            <Tooltip withinPortal label={info} withArrow>
              <Center>
                <Icon
                  icon="InfoIcon"
                  size={12}
                  color={(theme) => theme.colors[color][9]}
                />
              </Center>
            </Tooltip>
          ) : undefined
        }
      >
        {label}
      </Badge>
      {isArchived && (
        <Badge
          color="orange"
          leftIcon={
            <Icon
              icon="EyeSlashIcon"
              size={12}
              color={(theme) => theme.colors.orange[9]}
            />
          }
          tooltip="This reservation is archived and excluded from accounting."
        >
          Archived
        </Badge>
      )}
    </Group>
  );
};
